import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Progress,
  Result,
  Statistic,
  Steps,
  Table,
  Tabs,
  Tag,
  Upload,
  message,
} from "antd";
import { InboxOutlined, ReloadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import {
  ImportWrapper,
  PurposeList,
  Section,
  StatCard,
  StatsRow,
  ToolbarRow,
  TwoColumns,
} from "./styled";
import {
  getImportJob,
  getImportJobs,
  postImportPreview,
  postImportStart,
} from "../../api/import";

const MAX_BYTES = 20 * 1024 * 1024;
const POLL_MS = 2000;

const STATUS_COLOR = {
  pending: "default",
  running: "processing",
  done: "success",
  failed: "error",
};

const PurposePanel = () => {
  const { t } = useTranslation();
  return (
    <Section>
      <h4>{t("dataImport.purposeTitle")}</h4>
      <PurposeList>
        <li>{t("dataImport.purposeUpload")}</li>
        <li>{t("dataImport.purposePreview")}</li>
        <li>{t("dataImport.purposeImport")}</li>
      </PurposeList>
    </Section>
  );
};

const StatusTag = ({ status }) => {
  const { t } = useTranslation();
  const label = {
    pending: t("dataImport.statusPending"),
    running: t("dataImport.statusRunning"),
    done: t("dataImport.statusDone"),
    failed: t("dataImport.statusFailed"),
  }[status];
  return <Tag color={STATUS_COLOR[status]}>{label || status}</Tag>;
};

const ColumnsTable = ({ columns }) => {
  const { t } = useTranslation();
  const rows = [
    ...columns.missing_required.map((name) => ({ key: `m-${name}`, name, status: "missing" })),
    ...columns.unknown.map((name) => ({ key: `u-${name}`, name, status: "unknown" })),
    ...columns.matched.map((name) => ({ key: `ok-${name}`, name, status: "matched" })),
  ];
  const tagFor = (status) =>
    ({
      missing: <Tag color="red">{t("dataImport.missingRequired")}</Tag>,
      unknown: <Tag color="orange">{t("dataImport.unknown")}</Tag>,
      matched: <Tag color="green">{t("dataImport.matched")}</Tag>,
    }[status]);
  return (
    <Table
      size="small"
      pagination={{ pageSize: 8, size: "small" }}
      dataSource={rows}
      columns={[
        { title: t("dataImport.column"), dataIndex: "name", key: "name" },
        {
          title: t("dataImport.columnStatus"),
          dataIndex: "status",
          key: "status",
          width: 160,
          render: tagFor,
        },
      ]}
    />
  );
};

const SampleTable = ({ rows }) => {
  const { t } = useTranslation();
  if (!rows?.length) return null;
  const keys = Object.keys(rows[0]).slice(0, 12);
  return (
    <Section>
      <h4>{t("dataImport.sampleRows")}</h4>
      <Table
        size="small"
        pagination={false}
        scroll={{ x: true }}
        dataSource={rows.map((row, index) => ({ key: index, ...row }))}
        columns={keys.map((key) => ({
          title: key.split("/").pop(),
          dataIndex: key,
          key,
          ellipsis: true,
          render: (value) => (value === undefined ? "" : String(value)),
        }))}
      />
    </Section>
  );
};

const ErrorsTable = ({ errors }) => {
  const { t } = useTranslation();
  if (!errors?.length) return null;
  return (
    <Section>
      <h4>{t("dataImport.errorsTitle")}</h4>
      <Table
        size="small"
        pagination={{ pageSize: 10, size: "small" }}
        dataSource={errors.map((e, index) => ({ key: index, ...e }))}
        columns={[
          { title: t("dataImport.row"), dataIndex: "row", key: "row", width: 90 },
          { title: t("dataImport.reason"), dataIndex: "reason", key: "reason" },
        ]}
      />
    </Section>
  );
};

const JobResult = ({ job, onReset }) => {
  const { t } = useTranslation();
  const done = job.status === "done";
  const affected = job.affected || {};
  return (
    <>
      <Result
        status={done ? "success" : "error"}
        title={done ? t("dataImport.resultDone") : t("dataImport.resultFailed")}
        subTitle={done ? job.filename : job.error_message}
        extra={
          <Button type="primary" onClick={onReset}>
            {t("dataImport.newImport")}
          </Button>
        }
      />
      <StatsRow>
        <StatCard>
          <Statistic title={t("dataImport.inserted")} value={job.inserted} valueStyle={{ color: "#3f8600" }} />
        </StatCard>
        <StatCard>
          <Statistic title={t("dataImport.skipped")} value={job.skipped_duplicates} />
        </StatCard>
        <StatCard>
          <Statistic
            title={t("dataImport.failed")}
            value={job.failed}
            valueStyle={{ color: job.failed > 0 ? "#cf1322" : undefined }}
          />
        </StatCard>
      </StatsRow>
      {done && affected.quarters?.length > 0 && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t("dataImport.affected", {
            hospitals: affected.hospitals?.length || 0,
            provinces: affected.provinces?.length || 0,
            quarters: affected.quarters.join(", "),
          })}
        />
      )}
      <ErrorsTable errors={job.errors} />
    </>
  );
};

const ImportTab = ({ onFinished }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [checking, setChecking] = useState(false);
  const [job, setJob] = useState(null);
  const pollRef = useRef(null);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  useEffect(() => stopPolling, []);

  const reset = () => {
    stopPolling();
    setStep(0);
    setFile(null);
    setPreview(null);
    setJob(null);
  };

  const beforeUpload = async (selected) => {
    if (!selected.name.toLowerCase().endsWith(".xlsx")) {
      message.error(t("dataImport.onlyXlsx"));
      return Upload.LIST_IGNORE;
    }
    if (selected.size > MAX_BYTES) {
      message.error(t("dataImport.tooLarge"));
      return Upload.LIST_IGNORE;
    }
    setChecking(true);
    try {
      const result = await postImportPreview(selected);
      if (result?.status === "successful") {
        setFile(selected);
        setPreview(result);
        setStep(1);
      } else {
        message.error(result?.detail || t("dataImport.previewFailed"));
      }
    } catch (e) {
      message.error(t("dataImport.previewFailed"));
    } finally {
      setChecking(false);
    }
    return false;
  };

  const pollJob = useCallback(
    (jobId) => {
      stopPolling();
      pollRef.current = setInterval(async () => {
        try {
          const latest = await getImportJob(jobId);
          if (latest?.status !== "successful") return;
          setJob(latest);
          if (latest.status_code === "done" || latest.status_code === "failed") {
            stopPolling();
            onFinished?.();
          }
        } catch (e) {
          // keep polling; a transient network error should not end the import view
        }
      }, POLL_MS);
    },
    [onFinished]
  );

  const start = async () => {
    try {
      const result = await postImportStart(file);
      if (result?.status === "successful") {
        setJob({ id: result.job_id, status_code: "pending", progress_percent: 0, filename: file.name });
        setStep(2);
        pollJob(result.job_id);
      } else {
        message.error(result?.detail || t("dataImport.startFailed"));
      }
    } catch (e) {
      message.error(t("dataImport.startFailed"));
    }
  };

  const blocked = preview?.columns?.missing_required?.length > 0;
  const finished = job && (job.status_code === "done" || job.status_code === "failed");

  return (
    <>
      <Steps
        current={step}
        items={[
          { title: t("dataImport.stepUpload") },
          { title: t("dataImport.stepPreview") },
          { title: t("dataImport.stepImport") },
        ]}
      />

      {step === 0 && (
        <TwoColumns>
          <div>
            <Upload.Dragger
              accept=".xlsx"
              multiple={false}
              showUploadList={false}
              beforeUpload={beforeUpload}
              disabled={checking}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">{t("dataImport.dragText")}</p>
              <p className="ant-upload-hint">{t("dataImport.dragHint")}</p>
            </Upload.Dragger>
          </div>
          <PurposePanel />
        </TwoColumns>
      )}

      {step === 1 && preview && (
        <TwoColumns>
          <div>
            <StatsRow>
              <StatCard>
                <Statistic title={t("dataImport.totalRows")} value={preview.total_rows} />
              </StatCard>
              <StatCard>
                <Statistic
                  title={t("dataImport.importable")}
                  value={preview.importable}
                  valueStyle={{ color: "#3f8600" }}
                />
              </StatCard>
              <StatCard>
                <Statistic title={t("dataImport.duplicatesExisting")} value={preview.duplicates_existing} />
              </StatCard>
              <StatCard>
                <Statistic title={t("dataImport.duplicatesInFile")} value={preview.duplicates_in_file} />
              </StatCard>
            </StatsRow>
            {blocked && <Alert type="error" showIcon message={t("dataImport.cannotImport")} style={{ marginBottom: 16 }} />}
            {!blocked && preview.importable === 0 && (
              <Alert type="warning" showIcon message={t("dataImport.nothingToImport")} style={{ marginBottom: 16 }} />
            )}
            <ToolbarRow>
              <Button type="primary" onClick={start} disabled={blocked || preview.importable === 0}>
                {t("dataImport.startImport")}
              </Button>
              <Button onClick={reset}>{t("dataImport.chooseAnother")}</Button>
            </ToolbarRow>
            <Section>
              <h4>{t("dataImport.columns")}</h4>
              <ColumnsTable columns={preview.columns} />
            </Section>
            <SampleTable rows={preview.sample_rows} />
          </div>
          <PurposePanel />
        </TwoColumns>
      )}

      {step === 2 && job && !finished && (
        <Section>
          <h4>{job.status_code === "running" && job.progress_percent >= 99 ? t("dataImport.recalculating") : t("dataImport.importing")}</h4>
          <Progress percent={job.progress_percent || 0} status="active" />
          <p>
            {job.processed || 0} / {job.total_rows || 0}
          </p>
        </Section>
      )}

      {step === 2 && finished && <JobResult job={{ ...job, status: job.status_code }} onReset={reset} />}
    </>
  );
};

const HistoryTab = ({ refreshKey }) => {
  const { t } = useTranslation();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getImportJobs();
      setJobs(result?.jobs || []);
    } catch (e) {
      message.error(t("dataImport.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  return (
    <>
      <ToolbarRow>
        <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
          {t("dataImport.refresh")}
        </Button>
      </ToolbarRow>
      <Table
        size="small"
        loading={loading}
        locale={{ emptyText: t("dataImport.noJobs") }}
        dataSource={jobs.map((job) => ({ key: job.id, ...job }))}
        pagination={{ pageSize: 10, size: "small" }}
        columns={[
          { title: t("dataImport.time"), dataIndex: "created_at", key: "created_at", width: 170, render: (v) => (v ? new Date(v).toLocaleString() : "-") },
          { title: t("dataImport.file"), dataIndex: "filename", key: "filename" },
          { title: t("dataImport.uploadedBy"), dataIndex: "uploaded_by", key: "uploaded_by", width: 180 },
          { title: t("dataImport.status"), dataIndex: "status", key: "status", width: 120, render: (s) => <StatusTag status={s} /> },
          { title: t("dataImport.totalRows"), dataIndex: "total_rows", key: "total_rows", width: 100 },
          { title: t("dataImport.inserted"), dataIndex: "inserted", key: "inserted", width: 100 },
          { title: t("dataImport.skipped"), dataIndex: "skipped_duplicates", key: "skipped_duplicates", width: 130 },
          { title: t("dataImport.failed"), dataIndex: "failed", key: "failed", width: 90 },
        ]}
      />
    </>
  );
};

const DataImportPage = () => {
  const { t } = useTranslation();
  const [refreshKey, setRefreshKey] = useState(0);
  const onFinished = useCallback(() => setRefreshKey((k) => k + 1), []);

  return (
    <ImportWrapper>
      <Tabs
        defaultActiveKey="import"
        items={[
          { key: "import", label: t("dataImport.tabImport"), children: <ImportTab onFinished={onFinished} /> },
          { key: "history", label: t("dataImport.tabHistory"), children: <HistoryTab refreshKey={refreshKey} /> },
        ]}
      />
    </ImportWrapper>
  );
};

export default DataImportPage;
